import React from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { styled } from '@mui/system';
import ChevronRight from 'assets/icons/chevron_right.svg?react';

// Bottom-pinned slide nav. Single place for Prev/Next + the progress bar,
// works on desktop and mobile.

const Bar = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto',
  alignItems: 'center',
  gap: 16,
  padding: '6px 24px',
  borderTop: `1px solid ${theme.palette.grey[100]}`,
  background: theme.palette.white.main,
  zIndex: 5,
  [theme.breakpoints.down('sm')]: {
    padding: '4px 12px',
    gap: 8
  }
}));

const ProgressGroup = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  maxWidth: 560,
  margin: '0 auto'
}));

const KeyHint = styled('span')(({ theme }) => ({
  fontSize: 15,
  color: theme.palette.grey[300],
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  border: `1px solid ${theme.palette.grey[100]}`,
  padding: '2px 8px',
  borderRadius: 4,
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const ProgressLine = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flex: 1,
  minWidth: 0
}));

const Counter = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.grey[300],
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap'
}));

const Track = styled('div')(({ theme }) => ({
  flex: 1,
  height: 4,
  borderRadius: 999,
  background: theme.palette.grey[100],
  overflow: 'hidden'
}));

const Fill = styled('div')(({ theme, pct }) => ({
  width: `${pct}%`,
  height: '100%',
  background: theme.palette.primary.main,
  borderRadius: 999,
  transition: 'width 240ms ease'
}));

const NavButton = styled(Button)(({ theme, disabled }) => ({
  padding: '8px 16px',
  fontSize: 14,
  fontWeight: 500,
  borderRadius: 8,
  minWidth: 0,
  opacity: disabled ? 0.4 : 1,
  '& svg': {
    width: 18,
    height: 18,
    flexShrink: 0
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 12px',
    fontSize: 13,
    '& .label': {
      display: 'none'
    }
  }
}));

const ChevronLeftIcon = styled(ChevronRight)(() => ({
  transform: 'rotate(180deg)'
}));

const SlideFooter = ({ current, total, onPrev, onNext, prevLabel, nextLabel, hintLabel }) => {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;

  return (
    <Bar role="region" aria-label="Slide navigation">
      <NavButton
        variant="text"
        onClick={onPrev}
        disabled={!onPrev}
        aria-label={prevLabel || 'Previous'}
        startIcon={<ChevronLeftIcon />}>
        <span className="label">{prevLabel || 'Prev'}</span>
      </NavButton>

      <ProgressGroup>
        <ProgressLine aria-hidden="true">
          <Counter>{`${current} / ${total}`}</Counter>
          <Track>
            <Fill pct={pct} />
          </Track>
        </ProgressLine>
        {hintLabel ? <KeyHint aria-hidden="true">{hintLabel}</KeyHint> : null}
      </ProgressGroup>

      <NavButton
        variant="contained"
        color="primary"
        onClick={onNext}
        disabled={!onNext}
        aria-label={nextLabel || 'Next'}
        endIcon={<ChevronRight />}>
        <span className="label">{nextLabel || 'Next'}</span>
      </NavButton>
    </Bar>
  );
};

export default SlideFooter;
